import {Image, Link, RiverAccordion, Text} from '@primer/react-brand/esm'
import {useCallback, useState} from 'react'

import {getImageSources} from '@github-ui/swp-core/lib/utils/images'

import {AssetTypeEnum} from '../types'
import type {IconAssetLinkItem} from '../types'
import {AssetVideo} from './Video'
import {getAssetImageStyle, renderWithoutParagraphs, useDitherBackground} from '../utils'
import styles from './FlexSuiteRiverMedia.module.css'

export type FlexSuiteRiverAccordionProps = {
  items?: IconAssetLinkItem[]
}

function RiverAccordionMedia({item, isActive}: {item: IconAssetLinkItem; isActive: boolean}) {
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource, position} = item.fields
  const imageStyle = getAssetImageStyle(optionImage)
  const imagePosition = assetType === AssetTypeEnum.enum.Image ? position : undefined
  const {containerRef, DitherBackground} = useDitherBackground({
    className: styles.accordionDither,
    enabled: isActive && assetType === AssetTypeEnum.enum.Image && imagePosition !== 'fill',
  })
  const visibleContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      const visual = element?.parentElement
      if (!(visual instanceof HTMLDivElement) || visual.getAttribute('aria-hidden') !== 'true') return
      return containerRef(visual)
    },
    [containerRef],
  )

  return (
    <div ref={visibleContainerRef} className={styles.mediaFrame} data-image-position={imagePosition}>
      <DitherBackground />
      {assetType === AssetTypeEnum.enum.Image && optionImage && (
        <Image
          as="picture"
          aspectRatio={imageStyle ? 'custom' : undefined}
          src={`${optionImage.fields.file.url}?fm=webp`}
          sources={getImageSources(optionImage.fields.file.url, {maxWidth: 720})}
          style={imageStyle}
          alt={optionImage.fields.description ?? ''}
          loading="lazy"
        />
      )}
      <AssetVideo
        assetType={assetType}
        optionVideo={optionVideo}
        optionPoster={optionPoster}
        autoplay={autoplay}
        videoSource={videoSource}
      />
    </div>
  )
}

export const FlexSuiteRiverAccordion = ({items}: FlexSuiteRiverAccordionProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  if (!items || items.length === 0) return null

  return (
    <RiverAccordion variant="gridline">
      {items.map((item, index) => {
        const {heading, description, href, label, alt} = item.fields

        return (
          <RiverAccordion.Item key={item.sys.id}>
            <RiverAccordion.Heading as="h3" onClick={() => setActiveIndex(index)}>
              {renderWithoutParagraphs(heading)}
            </RiverAccordion.Heading>
            <RiverAccordion.Content>
              {description && <Text>{renderWithoutParagraphs(description)}</Text>}
              {href && label ? (
                <Link href={href} {...(alt ? {'aria-label': alt} : {})}>
                  {label}
                </Link>
              ) : null}
            </RiverAccordion.Content>
            <RiverAccordion.Visual className={styles.visual}>
              <RiverAccordionMedia item={item} isActive={index === activeIndex} />
            </RiverAccordion.Visual>
          </RiverAccordion.Item>
        )
      })}
    </RiverAccordion>
  )
}
